import React from "react";

import {
  PieChart,
  Pie,
  Tooltip,
  LineChart,
  ScatterChart,
  BarChart,
  Bar,
  Line,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Cell,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  FunnelChart,
  Funnel,
  LabelList,
  Treemap,
  AreaChart,
  Area,
  Text,
} from "recharts";

import { Table, Pagination } from "clearsense-ui/components";
import { abbreviateNumber } from "components/DataConsumption/utils";

import { COLORS } from "../../../constants";

const renderTable = ({
  chartData,
  tables,
  tablePageInfo,
  setTablePageInfo,
}) => (
  <>
    <Table
      fixedHeight
      key={chartData.id}
      totalCount={tables[chartData.id].length}
      currentPageNumber={tablePageInfo.pageNumber[chartData.id]}
      defaultPageSize={tablePageInfo.pageSize[chartData.id]}
      allowRowClick={false}
      paginationProps={{ className: "hidden" }}
      columnData={Object.keys(tables?.[chartData.id]?.[0]).map(columnName => ({
        key: columnName,
        dataIndex: columnName,
        title: columnName,
        width: Math.max(12 * columnName.length, 150),
        ellipsis: true,
        render: cellValue => cellValue || "N/A",
      }))}
      rowData={tables[chartData.id]}
      className="text-xs"
      rowSelection={false}
    />
    {tables[chartData.id].length > 100 && (
      <Pagination
        className="justify-end overflow-x-hidden"
        currentPage={tablePageInfo.pageNumber[chartData.id]}
        pageSize={tablePageInfo.pageSize[chartData.id]}
        totalRecordsCount={tables[chartData.id].length}
        setCurrentPage={current => {
          setTablePageInfo(prevState => ({
            ...prevState,
            pageNumber: {
              ...prevState.pageNumber,
              [chartData.id]: current,
            },
          }));
        }}
        setPageSize={pageSize => {
          setTablePageInfo(prevState => ({
            ...prevState,
            pageSize: {
              ...prevState.pageSize,
              [chartData.id]: pageSize,
            },
          }));
        }}
      />
    )}
  </>
);

const renderChart = ({
  chartData,
  tables,
  tablePageInfo,
  setTablePageInfo,
}) => {
  const chartComponent = {
    pie: { parent: PieChart, child: Pie },
    bar: { parent: BarChart, child: Bar },
    line: { parent: LineChart, child: Line },
    scatter: { parent: ScatterChart, child: Scatter },
    radar: { parent: RadarChart, child: Radar },
    funnel: { parent: FunnelChart, child: Funnel },
    area: { parent: AreaChart, child: Area },
  };
  const ChartParent = chartComponent?.[chartData.chart_type]?.parent;
  const ChartChild = chartComponent?.[chartData.chart_type]?.child;

  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
  }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  switch (chartData.chart_type) {
    case "pie":
      return (
        <ResponsiveContainer>
          <ChartParent>
            <ChartChild
              data={tables[chartData.id]}
              nameKey={chartData.namekey.split(",")[0]}
              dataKey={chartData.valuekey}
              labelLine={false}
              label={renderCustomizedLabel}
              isAnimationActive={false}
            >
              {tables[chartData.id]?.map((_, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </ChartChild>
            <Legend
              formatter={val => (
                <span className="capitalize">{val?.split("_")?.join(" ")}</span>
              )}
            />
            <Tooltip
              contentStyle={{ backgroundColor: "rgba(255, 255, 255, 0.9)" }}
            />
          </ChartParent>
        </ResponsiveContainer>
      );

    case "bar":
    case "line":
    case "scatter": {
      const lineChartProp =
        chartData.chart_type === "line" ? { type: "monotone" } : {};

      return (
        <ResponsiveContainer>
          <ChartParent
            data={tables[chartData.id]}
            margin={{ top: 10, right: 40, left: 10, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey={rowData =>
                Object.entries(rowData)
                  .filter(item => chartData.namekey.includes(item[0]))
                  .map(item => item[1] || "N/A")
                  .join(", ")
              }
              interval={0}
              height={60}
              tick={({ x, y, payload }) => (
                <Text
                  x={x}
                  y={y}
                  width={40}
                  angle={20}
                  textAnchor="start"
                  verticalAnchor="start"
                  fontSize={14}
                  className="capitalize"
                >
                  {payload?.value?.split("_")?.join(" ")}
                </Text>
              )}
            />
            <YAxis
              interval={0}
              tick={{ fontSize: 14 }}
              tickFormatter={val =>
                /amount|cost|expense/i.test(chartData.title)
                  ? `$${abbreviateNumber(val)}`
                  : abbreviateNumber(val)
              }
            />
            {chartData.chart_type === "scatter" ? (
              <Tooltip
                formatter={value => `${value}`}
                labelFormatter={() => ``}
                contentStyle={{ backgroundColor: "rgba(255, 255, 255, 0.9)" }}
              />
            ) : (
              <Tooltip
                contentStyle={{ backgroundColor: "rgba(255, 255, 255, 0.9)" }}
              />
            )}
            <Legend
              formatter={val => (
                <span className="capitalize">{val.split("_").join(" ")}</span>
              )}
            />
            {chartData.valuekey.split(", ").map((valuekey, idx) => (
              <ChartChild
                key={idx}
                dataKey={valuekey}
                fill={COLORS[idx % COLORS.length]}
                stroke={COLORS[idx % COLORS.length]}
                {...lineChartProp}
              />
            ))}
          </ChartParent>
        </ResponsiveContainer>
      );
    }

    case "radar":
      return (
        <ResponsiveContainer>
          <RadarChart
            cx="50%"
            cy="50%"
            outerRadius="80%"
            data={tables[chartData.id]}
          >
            <PolarGrid />
            <PolarAngleAxis
              dataKey={rowData =>
                Object.entries(rowData)
                  .filter(item => chartData.namekey.includes(item[0]))
                  .map(item => item[1] || "Nil")
                  .join(", ")
              }
            />
            <PolarRadiusAxis />
            {chartData.valuekey.split(", ").map((valuekey, idx) => (
              <Radar
                key={idx}
                name={valuekey}
                dataKey={valuekey}
                stroke={COLORS[idx % COLORS.length]}
                fill={COLORS[idx % COLORS.length]}
                fillOpacity={0.6}
              />
            ))}
            <Legend
              formatter={val => (
                <span className="capitalize">{val.split("_").join(" ")}</span>
              )}
            />
            <Tooltip
              contentStyle={{ backgroundColor: "rgba(255, 255, 255, 0.9)" }}
            />
          </RadarChart>
        </ResponsiveContainer>
      );

    case "funnel":
      return (
        <ResponsiveContainer>
          <FunnelChart>
            <Tooltip
              contentStyle={{ backgroundColor: "rgba(255, 255, 255, 0.9)" }}
            />
            <Funnel
              dataKey={chartData.valuekey}
              nameKey={rowData =>
                Object.entries(rowData)
                  .filter(item => chartData.namekey.includes(item[0]))
                  .map(item => item[1] || "Nil")
                  .join(", ")
              }
              data={tables[chartData.id].map((row, idx) => ({
                ...row,
                fill: COLORS[idx % COLORS.length],
              }))}
              isAnimationActive
            >
              <LabelList
                position="right"
                fill="#000"
                stroke="none"
                dataKey={rowData =>
                  Object.entries(rowData)
                    .filter(item => chartData.namekey.includes(item[0]))
                    .map(item => item[1] || "Nil")
                    .join(", ")
                }
              />
            </Funnel>
          </FunnelChart>
        </ResponsiveContainer>
      );

    case "tabular": {
      return (
        <div
          style={{
            width: "100%",
            height:
              tables?.[chartData.id]?.length > 100
                ? "94%"
                : "calc(100% - 40px)",
          }}
        >
          {renderTable({
            chartData,
            tables,
            tablePageInfo,
            setTablePageInfo,
          })}
        </div>
      );
    }

    case "tree": {
      return (
        <ResponsiveContainer>
          <Treemap
            width={400}
            height={200}
            data={tables[chartData.id]}
            nameKey={chartData.namekey.split(",")[0]}
            dataKey={chartData.valuekey}
            ratio={4 / 3}
            stroke="#fff"
            fill="#8884d8"
          >
            <Tooltip
              contentStyle={{ backgroundColor: "rgba(255, 255, 255, 0.9)" }}
            />
          </Treemap>
        </ResponsiveContainer>
      );
    }

    case "area": {
      return (
        <ResponsiveContainer>
          <ChartParent width={400} height={200} data={tables[chartData.id]}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey={rowData =>
                Object.entries(rowData)
                  .filter(item => chartData.namekey.includes(item[0]))
                  .map(item => item[1] || "N/A")
                  .join(", ")
              }
            />
            <YAxis />
            <Tooltip
              contentStyle={{ backgroundColor: "rgba(255, 255, 255, 0.9)" }}
            />
            {chartData.valuekey.split(", ").map((valuekey, idx) => (
              <ChartChild
                key={idx}
                dataKey={valuekey}
                fill={COLORS[idx % COLORS.length]}
                stroke={COLORS[idx % COLORS.length]}
              />
            ))}
          </ChartParent>
        </ResponsiveContainer>
      );
    }
  }
  //eslint fix
  return null;
};

export { renderChart };
